import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import { ReduxStateType } from '../../redux/store';
import {Mention} from "./ChatInterface/types";
import {MentionSearchType} from "./types/mentionSearch";
import {useChatService} from "../../services";
import {setMentionLoadingMore, setMentionSearchLoading, setMentionSearchResults,} from '../../redux/slices/chat';
import { useDebouncedCallback } from '../../hooks/useDebouncedCallback';
import { getAiEntityType } from '../../utils/entityDisplayUtils';

export const useMentionFunctionality = (inputTextRef: React.RefObject<HTMLTextAreaElement>) => {
  const { searchProjectsRoadmapsTeamsResources } = useChatService();
  const dispatch = useDispatch();

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const latestRequestIdRef = useRef<number>(0);
  const forcedSearchTypeRef = useRef<MentionSearchType | null>(null);

  const [inputText, setInputText] = useState('');


  const mentionSearchCache = useSelector((state: ReduxStateType) => state.chatMessages?.mentionSearchCache);
  console.log("mentionSearchCache", mentionSearchCache);
  const loadingSearch = useSelector((state: ReduxStateType) => state.chatMessages?.mentionSearchLoading);
  const loadingMore = useSelector((state: ReduxStateType) => state.chatMessages?.mentionLoadingMore);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [mentionStartPos, setMentionStartPos] = useState<number>(-1);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState<number>(-1);
  const [showDefaultOptions, setShowDefaultOptions] = useState<boolean>(false);
  const [currentSearchMode, setCurrentSearchMode] = useState<'default' | 'resources' | 'workunits' | 'roadmaps'>('default');
  const [isDropdownFromButton, setIsDropdownFromButton] = useState<boolean>(false);

  const [mentions, setMentions] = useState<Mention[]>([]);

  const [currentSearchType, setCurrentSearchType] = useState<MentionSearchType>(MentionSearchType.ALL);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>(''); 
  const [perPage] = useState<number>(20);
  const currentCache = mentionSearchCache?.[currentSearchType] || { items: [], searchTerm: '', page: 0, totalCount: 0, hasMore: false };
  const suggestions = currentCache.items;
  const createMentionWithSpacing = useCallback((selectedItem: any, beforeText: string, afterText: string, insertPos: number) => {
    const baseName = selectedItem?.name || '';
    const firstProject = Array.isArray(selectedItem?.projects) && selectedItem.projects.length > 0 ? selectedItem.projects[0] : '';
    const displayName = selectedItem?.type === 'team' && baseName
      ? `${baseName}${firstProject ? ` (${firstProject})` : ''}`
      : baseName;
    const mentionText = `@${displayName}`;

    const needsSpaceBefore = insertPos > 0 &&
      beforeText.length > 0 &&
      beforeText[beforeText.length - 1] !== ' ' &&
      beforeText[beforeText.length - 1] !== '\n' &&
      beforeText[beforeText.length - 1] !== '\r';

    const needsSpaceAfter = afterText.length > 0 &&
      afterText[0] !== ' ' &&
      afterText[0] !== '\n' &&
      afterText[0] !== '\r';

    const spacePrefix = needsSpaceBefore ? ' ' : '';
    const spaceSuffix = needsSpaceAfter ? ' ' : '';

    return {
      fullText: spacePrefix + mentionText + spaceSuffix,
      mentionText,
      spacePrefix,
      spaceSuffix,
      actualMentionStart: insertPos + spacePrefix.length
    };
  }, []);

  const defaultCategories: any[] = [];

  const transformResponseToItems = useCallback((response: any) => {
    const workUnits: any[] = [];
    const epics: any[] = [];
    const teams: any[] = [];
    const resources: any[] = [];
    const strategymaps: any[] = [];
    const features: any[] = [];

    if (response.projects) {
      response.projects.forEach((workunit: any) => {
        workUnits.push({
          ...workunit,
          type: 'work_unit',
        });
      });
    }

    if (response.strategymaps) {
      response.strategymaps.forEach((strategymap: any) => {
        strategymaps.push({
          ...strategymap,
          type: 'strategymap',
        });
      });
    }
    if (response.epics) {
      response.epics.forEach((epic: any) => {
        epics.push({
          ...epic,
          type: 'epic',
          api_type: epic.type
        });
      });
    }
    if (response.features) {
      response.features.forEach((feature: any) => {
        features.push({
          ...feature,
          type: 'feature',
          api_type: feature.type
        });
      });
    }

    if (response.teams) {
      response.teams.forEach((team: any) => {
        teams.push({
          ...team,
          type: 'team',
          name: team.name,
          projects: team.project_names || []
        });
      });
    }

    if (response.resources) {
      response.resources.forEach((resource: any) => {
        resources.push({
          ...resource,
          type: 'resource',
          skills_name: resource.skill_names || []
        });
      });
    }

    return { workUnits, epics, strategymaps, teams, resources, features };
  }, []);

  const combineItems = useCallback((workUnits: any[], epics: any[], teams: any[], resources: any[], strategymaps: any[], features: any[]) => {
    const topWorkUnits = workUnits.slice(0, 2);
    const topEpics = epics.slice(0, 2);
    const topTeams = teams.slice(0, 2);
    const topResources = resources.slice(0, 2);
    const topStrategymaps = strategymaps.slice(0, 2);
    const topFeatures = features.slice(0, 2);

    const restWorkUnits = workUnits.slice(2);
    const restEpics = epics.slice(2);
    const restTeams = teams.slice(2);
    const restResources = resources.slice(2);
    const restStrategymaps = strategymaps.slice(2);
    const restFeatures = features.slice(2);

    return [
      ...topStrategymaps,
      ...topEpics,
      ...topFeatures,
      ...topTeams,
      ...topResources,
      ...restWorkUnits,
      ...restEpics,
      ...restTeams,
      ...restResources,
      ...restStrategymaps,
      ...restFeatures
    ];
  }, []);

  const calculatePaginationState = useCallback((
    searchType: MentionSearchType,
    response: any,
    workUnits: any[],
    epics: any[],
    teams: any[],
    resources: any[],
    strategymaps: any[],
    combinedResults: any[],
    features: any[],
  ) => {
    let totalCount = 0;
    let hasMore = false;

    switch (searchType) {
      case MentionSearchType.ALL:
        totalCount = Number(response.total_projects || 0) +
          Number(response.total_roadmaps || 0) +
          Number(response.total_teams || 0) +
          Number(response.total_resources || 0);
        hasMore = combinedResults.length > 0 && combinedResults.length < totalCount;
        break;

      case MentionSearchType.PROJECT:
        totalCount = Number(response.total_projects || 0);
        hasMore = workUnits.length > 0 && workUnits.length < totalCount;
        break;

      case MentionSearchType.STRATEGY_MAP:
        totalCount = Number(response.total_strategymaps || 0);
        hasMore = strategymaps.length > 0 && strategymaps.length < totalCount;
        break;
      case MentionSearchType.EPIC:
        totalCount = Number(response.total_epics || 0);
        hasMore = epics.length > 0 && epics.length < totalCount;
        break;
      case MentionSearchType.FEATURE:
        totalCount = Number(response.total_features || 0);
        hasMore = features.length > 0 && features.length < totalCount;
        break;

      case MentionSearchType.TEAM:
        totalCount = Number(response.total_teams || 0);
        hasMore = teams.length > 0 && teams.length < totalCount;
        break;

      case MentionSearchType.RESOURCE:
        totalCount = Number(response.total_resources || 0);
        hasMore = resources.length > 0 && resources.length < totalCount;
        break;
    }

    return { totalCount, hasMore };
  }, []);

  useEffect(() => {
    if (showSuggestions && suggestions && suggestions.length > 0) {
      setSelectedSuggestionIndex(0);
    } else if (showDefaultOptions) {
      setSelectedSuggestionIndex(0);
    } else {
      setSelectedSuggestionIndex(-1);
    }
  }, [suggestions, showSuggestions, showDefaultOptions]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleFetchSuggestions = useCallback(async (term: string = '', searchType: MentionSearchType = MentionSearchType.ALL, requestId?: number) => {
    const searchTerm = term || '';
    setCurrentSearchType(searchType);

    const cache = mentionSearchCache?.[searchType];
    const isCacheValid = cache && cache.items.length > 0 && cache.searchTerm === searchTerm;

    if (isCacheValid) {
      dispatch(setMentionSearchLoading(false));
      return;
    }

    if (cache && cache.searchTerm !== searchTerm) {
      dispatch(setMentionSearchResults({
        searchType,
        searchTerm: '',
        items: [],
        page: 0,
        totalCount: 0,
        hasMore: false,
        append: false
      }));
    }

    dispatch(setMentionSearchLoading(true));

    try {
      const response = await searchProjectsRoadmapsTeamsResources(searchTerm, searchType, 1, perPage);
      const { workUnits, epics, teams, resources, strategymaps, features } = transformResponseToItems(response);

      const combinedResults = combineItems(workUnits, epics, teams, resources, strategymaps, features);

      const { totalCount, hasMore } = calculatePaginationState(
        searchType,
        response,
        workUnits,
        epics,
        teams,
        resources,
        strategymaps,
        combinedResults,
        features
      );

      // Ignore stale responses that are not the latest request
      if (requestId !== undefined && requestId !== latestRequestIdRef.current) {
        return;
      }

      dispatch(setMentionSearchResults({
        searchType,
        searchTerm,
        items: combinedResults,
        page: 1,
        totalCount,
        hasMore,
        append: false
      }));
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      if (requestId === undefined || requestId === latestRequestIdRef.current) {
        dispatch(setMentionSearchResults({
          searchType,
          searchTerm,
          items: [],
          page: 1,
          totalCount: 0,
          hasMore: false,
          append: false
        }));
      }
    } finally {
      if (requestId === undefined || requestId === latestRequestIdRef.current) {
        dispatch(setMentionSearchLoading(false));
      }
    }
  }, [
    searchProjectsRoadmapsTeamsResources,
    perPage,
    mentionSearchCache,
    dispatch,
    transformResponseToItems,
    combineItems,
    calculatePaginationState
  ]);

  const debouncedRunSearch = useDebouncedCallback(
    (term: string, searchType: MentionSearchType) => {
      const requestId = ++latestRequestIdRef.current;

      if (inputTextRef.current && mentionStartPos !== -1) {
        const currentText = inputTextRef.current.value;
        const beforeCursor = currentText.substring(0, inputTextRef.current.selectionStart || 0);
        const lastAtIndex = beforeCursor.lastIndexOf('@');
        if (lastAtIndex !== -1) {
          const latestSearchTerm = beforeCursor.substring(lastAtIndex + 1);
          handleFetchSuggestions(latestSearchTerm, searchType, requestId);
          return;
        }
      }
      handleFetchSuggestions(term, searchType, requestId);
    },
    300,
    { leading: false, trailing: true, maxWait: 600 }
  );

  const handleSearch = useCallback((term: string, searchType: MentionSearchType = MentionSearchType.ALL) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setCurrentSearchType(searchType);
    forcedSearchTypeRef.current = searchType;
    setCurrentSearchTerm(term);
    const cache = mentionSearchCache?.[searchType];
    const searchTerm = term || '';
    const shouldFetch = !cache || cache.items.length === 0 || cache.searchTerm !== searchTerm;

    if (shouldFetch) {
      if (cache && cache.searchTerm !== searchTerm) {
        dispatch(setMentionSearchResults({
          searchType,
          searchTerm: '',
          items: [],
          page: 0,
          totalCount: 0,
          hasMore: false,
          append: false
        }));
      }
      dispatch(setMentionSearchLoading(true));
    }

    debouncedRunSearch(term, searchType);
  }, [mentionSearchCache, dispatch, debouncedRunSearch]);

  const loadMore = useCallback(async () => {
    if (loadingMore) return;

    const cache = mentionSearchCache?.[currentSearchType];
    if (!cache || !cache.hasMore) return;

    const nextPage = cache.page + 1;

    dispatch(setMentionLoadingMore(true));
    try {
      const response = await searchProjectsRoadmapsTeamsResources(
        cache.searchTerm,
        currentSearchType,
        nextPage,
        perPage
      );

      const { workUnits, epics, teams, resources, strategymaps, features } = transformResponseToItems(response);

      // Combine items with top 2 from each category first
      const combinedResults = combineItems(workUnits, epics, teams, resources, strategymaps, features);

      if (combinedResults.length === 0) {
        dispatch(setMentionSearchResults({
          searchType: currentSearchType,
          searchTerm: cache.searchTerm,
          items: [],
          page: nextPage,
          totalCount: cache.totalCount,
          hasMore: false,
          append: true
        }));
        return;
      }

      const currentItemsCount = cache.items.length + combinedResults.length;
      const hasMore = currentItemsCount < cache.totalCount;

      dispatch(setMentionSearchResults({
        searchType: currentSearchType,
        searchTerm: cache.searchTerm,
        items: combinedResults,
        page: nextPage,
        totalCount: cache.totalCount,
        hasMore,
        append: true
      }));
    } catch (error) {
      console.error('Error loading more:', error);
    } finally {
      dispatch(setMentionLoadingMore(false));
    }
  }, [
    loadingMore,
    currentSearchType,
    mentionSearchCache,
    perPage,
    searchProjectsRoadmapsTeamsResources,
    dispatch,
    transformResponseToItems,
    combineItems
  ]);

  const closeMentionDropdown = useCallback(() => {
    setShowSuggestions(false);
    setShowDefaultOptions(false);
    setMentionStartPos(-1);
    setSelectedSuggestionIndex(-1);
    setCurrentSearchMode('default');
    setIsDropdownFromButton(false);
    setCurrentSearchTerm(''); 
    forcedSearchTypeRef.current = null;
  }, []);

  const getTextWithContextTags = useCallback(() => {
    if (mentions.length === 0) {
      return inputText.trim();
    }
    let result = inputText;
    const sortedMentions = [...mentions].sort((a, b) => b.start - a.start);

    sortedMentions.forEach(mention => {
      const payload: any = {
        type: mention.type,
        id: String(mention.entity_id),
        name: mention.name
      };
      if (mention.type === 'team') {
        payload.team_name = mention.team_name || mention.name;
        payload.project_name = mention.project_name
      }
      const contextTag = `<context>${JSON.stringify(payload)}</context>`;
      const mentionText = `@${mention.name}`;
      result = result.replace(mentionText, contextTag);
    });
    return result.trim();
  }, [inputText, mentions]);

  const clearMentions = useCallback(() => {
    setMentions([]);
  }, []);

  const convertContextTagsToMentions = useCallback((text: string) => {
    if (!text) return text;
    const contextTagRegex = /<context>(.*?)<\/context>/g;
    const result = text.replace(contextTagRegex, (match, jsonStr) => {
      try {
        const obj = JSON.parse(jsonStr);
        if (obj && obj.type === 'team') {
          const baseName = obj.team_name || obj.name;
          const label = baseName ? `${baseName}${obj.project_name ? ` (${obj.project_name})` : ''}` : '';
          return label ? `@${label}` : match;
        }
        const displayName = obj?.name;
        if (displayName) {
          return `@${displayName}`;
        }
        return match;
      } catch (e) {
        const legacyMatch = match.match(/<context>\{"type":\s*"([^"]+)",\s*"id":\s*"([^"]+)",\s*"name":\s*"([^"]+)"\}<\/context>/);
        if (legacyMatch) {
          return `@${legacyMatch[3]}`;
        }
        return match;
      }
    });
    return result;
  }, []);

  const scrollSelectedItemIntoView = useCallback((index: number) => {
    if (dropdownRef.current && index >= 0) {
      const dropdown = dropdownRef.current;
      const selectedElement = dropdown.querySelector(`[data-suggestion-index="${index}"]`);

      if (selectedElement) {
        const dropdownRect = dropdown.getBoundingClientRect();
        const elementRect = selectedElement.getBoundingClientRect();
        if (elementRect.top < dropdownRect.top) {
          selectedElement.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
        else if (elementRect.bottom > dropdownRect.bottom) {
          selectedElement.scrollIntoView({ block: 'end', behavior: 'smooth' });
        }
      }
    }
  }, []);

  const updateMentionPositions = useCallback((newText: string) => {
    const newMentions: Mention[] = [];
    if (mentions.length > 0) {
      const mentionDataByName = new Map<string, any>();
      mentions.forEach(m => {
        if (!mentionDataByName.has(m.name)) {
          mentionDataByName.set(m.name, m);
        }
      });
      mentionDataByName.forEach((mention, name) => {
        const mentionText = `@${name}`;
        let lastIndex = -1;
        while ((lastIndex = newText.indexOf(mentionText, lastIndex + 1)) !== -1) {
          newMentions.push({
            ...mention,
            start: lastIndex,
            end: lastIndex + mentionText.length,
            completed: mention.completed || false
          });
        }
      });
      newMentions.sort((a, b) => a.start - b.start);
    }
    setMentions(newMentions);
  }, [mentions]);

  const handleInputChange = useCallback((e: any) => {
    const value: string = e.target.value;
    const cursor: number = e.target.selectionStart;
    
      if (mentions.length > 0 && value.length < inputText.length) {
        const deletedMention = mentions.find(mention => {
          if (!mention.completed) return false;
          const mentionText = `@${mention.name}`;
          const wasMentionDeleted = !value.includes(mentionText) && inputText.includes(mentionText);
          return wasMentionDeleted;
        });
        if (deletedMention) {
          const newMentions = mentions.filter(m => m.id !== deletedMention.id || m.start !== deletedMention.start);
          setMentions(newMentions);
        }
      }
      updateMentionPositions(value);
    
    setInputText(value);
    
    const beforeCursor = value.substring(0, cursor);
    const lastAtIndex = beforeCursor.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const completedMentionAtPosition = mentions.find(mention =>
        mention.completed && mention.start === lastAtIndex
      );
      if (completedMentionAtPosition) {
        closeMentionDropdown();
        return;
      }
      const beforeAt = beforeCursor.substring(0, lastAtIndex);
      const lastChar = beforeAt.length > 0 ? beforeAt[beforeAt.length - 1] : '';
      if (lastAtIndex === 0 || lastChar === ' ' || lastChar === '\n' || lastChar === '\r') {
        const afterAt = beforeCursor.substring(lastAtIndex + 1);
        const hasDoubleSpace = afterAt.includes('  ');
        const hasNewline = afterAt.includes('\n') || afterAt.includes('\r');
        const cursorMovedAwayFromMention = cursor < lastAtIndex || cursor > lastAtIndex + afterAt.length + 1;
        if (!hasDoubleSpace && !hasNewline && !cursorMovedAwayFromMention) {
          setMentionStartPos(lastAtIndex);
            setSelectedSuggestionIndex(-1);
          forcedSearchTypeRef.current = forcedSearchTypeRef.current ?? null;
          const effectiveSearchType = forcedSearchTypeRef.current ?? MentionSearchType.ALL;
          if (afterAt.length === 0) {
            setCurrentSearchMode('default');
            setShowSuggestions(true);
            setIsDropdownFromButton(false);
            handleSearch('', effectiveSearchType);
          } else {
            setShowSuggestions(true);
            setIsDropdownFromButton(false);
            handleSearch(afterAt, effectiveSearchType);
          }
        } else {
          closeMentionDropdown();
        }
      } else {
        closeMentionDropdown();
      }
    } else {
      closeMentionDropdown();
    }
  }, [handleSearch, setInputText, updateMentionPositions, mentions, inputText, closeMentionDropdown, currentSearchType]);

  const handleRoadmapSelectInternal = useCallback((selectedItem: any, options: { fromButton?: boolean } = {}) => {
    const { fromButton = false } = options;
    if (!inputTextRef.current || !selectedItem) return;
    const textarea = inputTextRef.current;
    const currentValue = textarea.value;
    const currentCursorPos = textarea.selectionStart || 0;
    
    if (fromButton || (isDropdownFromButton && mentionStartPos === -1)) {
      const baseText = currentValue;
      const insertPos = currentCursorPos;
      const beforeText = baseText.substring(0, insertPos);
      const afterText = baseText.substring(insertPos);
      const spacing = createMentionWithSpacing(selectedItem, beforeText, afterText, insertPos);
      const mentionStart = spacing.actualMentionStart;
      const newInputText =
        baseText.slice(0, insertPos) +
        spacing.fullText +
        baseText.slice(insertPos);

      const aiEntityType = getAiEntityType(selectedItem.entity_type, selectedItem.api_type, selectedItem.type);
      const newMention: Mention = {
        id: selectedItem.id || selectedItem.entity_id,
        name: (selectedItem.type === 'team' && selectedItem.name) ? `${selectedItem.name}${Array.isArray(selectedItem.projects) && selectedItem.projects[0] ? ` (${selectedItem.projects[0]})` : ''}` : selectedItem.name,
        type: aiEntityType || 'strategy_map',
        entity_id: selectedItem.entity_id || selectedItem.id,
        start: mentionStart,
        end: mentionStart + spacing.mentionText.length,
        completed: true
      };
      if (newMention.type === 'team') {
        newMention.team_name = selectedItem.name;
        const projectsArray = Array.isArray(selectedItem.projects) ? selectedItem.projects : [];
        newMention.project_name = projectsArray.length > 0 ? projectsArray[0] : '';
      }
      const adjustedMentions = mentions.map((existingMention: any) => {
        if (existingMention.start >= insertPos) {
          return {
            ...existingMention,
            start: existingMention.start + spacing.fullText.length,
            end: existingMention.end + spacing.fullText.length
          };
        }
        return existingMention;
      });
      const newMentions = [...adjustedMentions, newMention];
      newMentions.sort((a, b) => a.start - b.start);
      setInputText(newInputText);
      setMentions(newMentions);
      closeMentionDropdown();
      setTimeout(() => {
        const newCursorPos = mentionStart + spacing.mentionText.length + spacing.spaceSuffix.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
        textarea.focus();
      }, 0);
    } else {
      if (mentionStartPos === -1) {
        console.warn('No mention start position found for @ typing scenario');
        return;
      }
      const replaceEndPos = textarea.selectionStart || currentValue.length;
      const beforeMention = currentValue.substring(0, mentionStartPos);
      const afterReplacement = currentValue.substring(replaceEndPos);
      const spacing = createMentionWithSpacing(selectedItem, beforeMention, afterReplacement, mentionStartPos);
      const newInputText = beforeMention + spacing.fullText + afterReplacement;
      const actualMentionStart = spacing.actualMentionStart;
      
      const aiEntityType = getAiEntityType(selectedItem.entity_type, selectedItem.api_type, selectedItem.type);
      const newMention: Mention = {
        id: selectedItem.id || selectedItem.entity_id,
        name: (selectedItem.type === 'team' && selectedItem.name) ? `${selectedItem.name}${Array.isArray(selectedItem.projects) && selectedItem.projects[0] ? ` (${selectedItem.projects[0]})` : ''}` : selectedItem.name,
        type: aiEntityType || 'strategy_map',
        entity_id: selectedItem.entity_id || selectedItem.id,
        start: actualMentionStart,
        end: actualMentionStart + spacing.mentionText.length,
        completed: true
      };
      if (newMention.type === 'team') {
        newMention.team_name = selectedItem.name;
        const projectsArray = Array.isArray(selectedItem.projects) ? selectedItem.projects : [];
        newMention.project_name = projectsArray.length > 0 ? projectsArray[0] : '';
      }
      const newMentions = [
        ...mentions.filter((m: any) => {
          const hasOverlap = !(m.end <= actualMentionStart || m.start >= newMention.end);
          return !hasOverlap;
        }),
        newMention
      ];
      setInputText(newInputText);
      setMentions(newMentions);
      closeMentionDropdown();
      setTimeout(() => {
        const newCursorPos = actualMentionStart + spacing.mentionText.length + spacing.spaceSuffix.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
        textarea.focus();
      }, 0);
    }
  }, [mentions, closeMentionDropdown, setInputText, mentionStartPos, createMentionWithSpacing, inputTextRef]);

  const handleDefaultCategorySelect = useCallback(() => {
  }, []);

  const handleKeyDown = useCallback((e: any) => {
    if ((showSuggestions && suggestions && suggestions.length > 0) || (showDefaultOptions && defaultCategories.length > 0)) {
      const currentList = showDefaultOptions ? defaultCategories : suggestions ? suggestions : [];
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedSuggestionIndex((prev: number) => {
          const newIndex = prev < currentList.length - 1 ? prev + 1 : 0;
          setTimeout(() => scrollSelectedItemIntoView(newIndex), 0);
          return newIndex;
        });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedSuggestionIndex((prev: number) => {
          const newIndex = prev > 0 ? prev - 1 : currentList.length - 1;
          setTimeout(() => scrollSelectedItemIntoView(newIndex), 0);
          return newIndex;
        });
        return;
      }
      if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
        e.preventDefault();
        if (showDefaultOptions) {
          handleDefaultCategorySelect();
        } else {
          const selectedItem = suggestions ? (suggestions as any)[selectedSuggestionIndex] : null;
          if (selectedItem.type === 'category') {
            handleDefaultCategorySelect();
          } else {
            handleRoadmapSelectInternal(selectedItem, {
              fromButton: isDropdownFromButton
            });
          }
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMentionDropdown();
        return;
      }
    }
  }, [showSuggestions, suggestions, showDefaultOptions, selectedSuggestionIndex, handleDefaultCategorySelect, handleRoadmapSelectInternal, scrollSelectedItemIntoView, isDropdownFromButton, closeMentionDropdown]);

  const triggerMentionDropdown = useCallback((searchType: MentionSearchType = MentionSearchType.ALL) => {
    if (!inputTextRef.current) return;
    const textarea = inputTextRef.current;
    const currentCursor = textarea.selectionStart || 0;
    const currentText = textarea.value || '';
    const beforeCursor = currentText.substring(0, currentCursor);
    const lastChar = beforeCursor.length > 0 ? beforeCursor[beforeCursor.length - 1] : '';
    let newText = currentText;
    let newCursorPos = currentCursor;
    let atPosition = -1;
    if (lastChar !== '@') {
      const needsSpace = beforeCursor.length > 0 && lastChar !== ' ' && lastChar !== '\n';
      const toInsert = needsSpace ? ' @' : '@';
      newText = currentText.substring(0, currentCursor) + toInsert + currentText.substring(currentCursor);
      newCursorPos = currentCursor + toInsert.length;
      atPosition = newCursorPos - 1;
    } else {
      atPosition = currentCursor - 1;
    }
    if (newText !== currentText) {
      setInputText(newText);
    }
    setMentionStartPos(atPosition);
    setCurrentSearchMode('default');
    setShowSuggestions(true);
    setSelectedSuggestionIndex(0);
    setIsDropdownFromButton(true);
    forcedSearchTypeRef.current = searchType ?? currentSearchType;
    handleSearch('', searchType ?? currentSearchType);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }, [setInputText, handleSearch, inputTextRef, currentSearchType]);

  const showMentionDropdownOnly = useCallback((searchType: MentionSearchType = MentionSearchType.ALL) => {
    setCurrentSearchMode('default');
    setShowSuggestions(true);
    setSelectedSuggestionIndex(0);
    setMentionStartPos(-1);
    setIsDropdownFromButton(true);
    forcedSearchTypeRef.current = searchType ?? currentSearchType;
    handleSearch('', searchType ?? currentSearchType);
  }, [handleSearch, currentSearchType]);

  const addMention = useCallback((mention: any) => {
    setMentions((prevMentions: Mention[]) => {
      const existingMention = prevMentions.find(m =>
        m.entity_id === mention.entity_id && m.start === mention.start
      );
      if (existingMention) {
        return prevMentions;
      }
      const newMentions = [...prevMentions, mention];
      return newMentions.sort((a, b) => a.start - b.start);
    });
  }, []);

  return {
    inputText,
    setInputText,
    suggestions,
    loadingSearch,
    loadingMore,
    showSuggestions,
    showDefaultOptions,
    selectedSuggestionIndex,
    defaultCategories,
    currentSearchMode,
    dropdownRef,
    mentions,
    isDropdownFromButton,
    mentionStartPos,
    currentPage: currentCache.page || 1,
    perPage,
    currentSearchType,
    currentSearchTerm,
    hasMoreAll: mentionSearchCache?.all?.hasMore || false,
    hasMoreEpics: mentionSearchCache?.epic?.hasMore || false,
    hasMoreFeatures: mentionSearchCache?.feature?.hasMore || false,
    hasMoreStrategyMaps: mentionSearchCache?.strategymap?.hasMore || false,
    hasMoreTeams: mentionSearchCache?.team?.hasMore || false,
    hasMoreResources: mentionSearchCache?.resource?.hasMore || false,
    handleInputChange,
    handleKeyDown,
    handleRoadmapSelectInternal,
    handleDefaultCategorySelect,
    closeMentionDropdown,
    getTextWithContextTags,
    clearMentions,
    convertContextTagsToMentions,
    triggerMentionDropdown,
    showMentionDropdownOnly,
    handleSearch,
    addMention,
    setMentions,
    loadMore
  };
};

